import React, { useEffect } from 'react';

import { Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { ProgressView } from '../../../../components/notifications/progress/ProgressView';
import { formatCurrency } from '../../../../utility/utils';
import { api, Booking, BookingErrorStatus, VehicleStatus } from '../../../../logic/api';
import { useDate } from "../../../../utility/useDate";

import ErrorDialog from "../../../../components/notifications/errorDialog/ErrorDialog";
import { ProgressDialog } from "../../../../components/notifications/progressDialog/progressDialog";
import { ISessionState } from '../../../../utility/useSession';
import { Trans, useTranslation } from 'react-i18next';

const CheckoutConfirmDialog: React.FunctionComponent<{
    booking: Booking | null,
    callbacks: {
        refreshSalesAndProducts: () => Promise<void>,
        showPaymentDialog: () => void
    },
    display: boolean,
    session: ISessionState,
    stateHooks: {
        setBooking: React.Dispatch<React.SetStateAction<Booking | null>>,
        setShowCheckOut: React.Dispatch<React.SetStateAction<boolean>>,
        setShowCheckOutComplete: React.Dispatch<React.SetStateAction<boolean>>,
        setBookingHistoryTableKey: React.Dispatch<React.SetStateAction<string | null>>,
        setAssetHistoryTableKey: React.Dispatch<React.SetStateAction<string | null>>,
    },
}> = (props) => {
    const { booking, callbacks, display, session, stateHooks } = props;
    const { t } = useTranslation();

    const earlyReturnEnabled = session.current.site?.preferences.early_return_enabled || false;

    const { refreshSalesAndProducts, showPaymentDialog } = callbacks;
    const { setBooking, setShowCheckOut, setShowCheckOutComplete, setBookingHistoryTableKey, setAssetHistoryTableKey } = stateHooks;
    // const [validationErrors, setValidationErrors] = useState<BookingStatusChangeError[]>([]);

    const { localizeDate } = useDate();

    const applyErrorActions = api.hooks.booking.applyStatusChangeErrorActions();
    const fetchBooking = api.hooks.booking.read();
    const getSalesAndPayments = api.hooks.booking.getSalesAndPayments();
    const requestCheckOutBooking = api.hooks.booking.checkOut();
    const preValidateCheckout = api.hooks.booking.preValidateStatusChange();

    const booking_checkout_with_balance_enabled = (session.current.site?.preferences.booking_checkout_with_balance_enabled === true);
    const amountDue = !getSalesAndPayments.data ? 0 : getSalesAndPayments.data.outstanding;
    const validationErrors = preValidateCheckout.data?.errors || [];

    //booking exists, vehicle in returns, pickByTime exists, pickByTime in future
    const earlyReturnError = validationErrors.find(error => error.status === BookingErrorStatus.EarlyReturn);

    useEffect(() => {
        if (!booking || !display) {
            return;
        }

        getSalesAndPayments.send(
            session.current.context,
            {
                bookingId: booking?.id,
            }
        );

        if (earlyReturnEnabled) {
            preValidateCheckout.send(
                session.current.context,
                {
                    id: booking.id,
                    vehicleStatus: VehicleStatus.CheckedOut
                }
            );
        }
    }, [display, booking])

    const getDialogText = () => {
        if (earlyReturnError) {
            return t("input.validaions.vehicleNotReady");
        }

        if (!booking_checkout_with_balance_enabled && amountDue > 0) {
            return t("input.validations.paymentRequired");
        }

        return t("input.validations.checkoutConfirmation");
    }

    const requestEarlyReturn = async () => {
        if (booking) {
            setShowCheckOut(false);
            applyErrorActions.send(session.current.context, { id: booking.id, errors: validationErrors }, () => {
                fetchBooking.send(session.current.context, { id: booking.id }, newBooking => {
                    setBooking(newBooking);
                });
            });
        }
    }

    const confirmCheckOut = () => {
        return new Promise((accept) => {
            setShowCheckOut(false);

            if (booking_checkout_with_balance_enabled == false && amountDue > 0) {
                showPaymentDialog();
                return;
            }

            if (booking) {
                requestCheckOutBooking.send(
                    session.current.context,
                    {
                        id: booking.id,
                        checkOutDate: localizeDate(new Date()).toISO() ?? "",
                    },
                    (updatedBooking) => {
                        if (updatedBooking == null) {
                            accept(false);
                            return;
                        }
                        setBooking(updatedBooking);
                        setShowCheckOutComplete(true);
                        setBookingHistoryTableKey(new Date().getTime().toString());
                        setAssetHistoryTableKey(new Date().getTime().toString());
                        refreshSalesAndProducts();
                        accept(true);
                    }
                );
            }
        })
    }

    return (<>
        <ErrorDialog err={requestCheckOutBooking.err}></ErrorDialog>
        <ProgressDialog
            status={requestCheckOutBooking.status}
        ></ProgressDialog>
        <Dialog fullWidth={true} maxWidth="md" open={display}>
            <ProgressView status={fetchBooking.status}>
                <ProgressView status={getSalesAndPayments.status}>
                    <ProgressView status={preValidateCheckout.status}>
                        <DialogTitle>{earlyReturnError ? t("booking.checkoutDialog.earlyReturn.title") : t("booking.checkoutDialog.paymentPending.title")}</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                {!earlyReturnError && amountDue > 0 && (<div style={{ color: "red", fontWeight: "bold", marginBottom: 20 }}>
                                    <Trans i18nKey="booking.checkoutDialog.paymentPending.content.amount">
                                        Outstanding balance of {{ amount: formatCurrency(amountDue, session) }}
                                    </Trans>
                                </div>)}

                                <div>{getDialogText()}</div>
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => {
                                    setShowCheckOut(false);
                                }}
                            >
                                {t("general.cancel")}
                            </Button>
                            {/* 
                                This has not been implemented yet.
                                {!earlyReturnError && (amountDue > 0) && (
                                <Button
                                    onClick={() => {
                                        requestPaymentDialog();
                                    }}
                                >
                                    <b>Add Payment</b>
                                </Button>
                            )} */}
                            {!earlyReturnError && (booking_checkout_with_balance_enabled || amountDue == 0) && (
                                <Button
                                    onClick={() => {
                                        confirmCheckOut();
                                    }}
                                >
                                    <b>{t("input.labels.checkOut")}</b>
                                </Button>
                            )}
                            {earlyReturnError && (
                                <Button
                                    onClick={() => {
                                        requestEarlyReturn();
                                    }}
                                >
                                    <b>{t("general.confirm")}</b>
                                </Button>
                            )}
                        </DialogActions>
                    </ProgressView>
                </ProgressView>
            </ProgressView>
        </Dialog>
    </>);
}

export default CheckoutConfirmDialog;
